import { createSHA256 } from "hash-wasm";

const CACHE_NAME = "sharemyai-model-pack-v1";
const CACHE_PREFIX = "/__model_cache__/";

export function assessModelPackRequirements({ catalog, capability, storageEstimate, crossOriginIsolated }) {
  const availableBytes = Math.max(0, Number(storageEstimate?.quota || 0) - Number(storageEstimate?.usage || 0));
  const storageRequired = Math.ceil(catalog.downloadBytes * 1.12);
  const bufferLimit = Number(capability?.limits?.maxBufferSize || 0);
  const storageLimit = Number(capability?.limits?.maxStorageBufferBindingSize || 0);
  const checks = {
    webgpu: Boolean(capability?.webgpuAvailable),
    shaderF16: capability?.features?.includes("shader-f16") || false,
    crossOriginIsolated: Boolean(crossOriginIsolated),
    bufferLimit: bufferLimit >= catalog.requiredBufferBytes,
    storageBindingLimit: storageLimit >= catalog.requiredBufferBytes,
    browserStorage: availableBytes >= storageRequired,
    artifactHashes: catalog.artifacts.every((artifact) => /^[0-9a-f]{64}$/.test(artifact.sha256 || ""))
  };
  const requiredChecks = { ...checks };
  delete requiredChecks.crossOriginIsolated;
  return {
    eligible: Object.values(requiredChecks).every(Boolean),
    checks,
    wasmThreads: checks.crossOriginIsolated ? Math.min(4, globalThis.navigator?.hardwareConcurrency || 1) : 1,
    availableBytes,
    storageRequired,
    downloadBytes: catalog.downloadBytes,
    largestArtifactBytes: catalog.requiredBufferBytes
  };
}

export async function inspectModelPack(catalog, capability) {
  if (!("storage" in navigator) || !("caches" in globalThis) || !("serviceWorker" in navigator)) {
    return {
      eligible: false,
      checks: { browserStorageApis: false },
      installedArtifacts: 0,
      totalArtifacts: catalog.artifacts.length
    };
  }
  const storageEstimate = await navigator.storage.estimate();
  const assessment = assessModelPackRequirements({ catalog, capability, storageEstimate, crossOriginIsolated: globalThis.crossOriginIsolated });
  const cache = await caches.open(CACHE_NAME);
  let installedArtifacts = 0;
  let installedBytes = 0;
  for (const artifact of catalog.artifacts) {
    const response = await cache.match(cacheUrl(artifact.path));
    if (artifactCacheMatches(response, artifact)) {
      installedArtifacts += 1;
      installedBytes += artifact.size;
    }
  }
  return { ...assessment, installedArtifacts, installedBytes, totalArtifacts: catalog.artifacts.length, installed: installedArtifacts === catalog.artifacts.length };
}

export async function installModelPack(catalog, { capability, onProgress = () => {}, signal } = {}) {
  const status = await inspectModelPack(catalog, capability);
  if (!status.eligible) throw new Error(describeFailedChecks(status.checks));
  await navigator.storage.persist?.();
  await navigator.serviceWorker.register("/model-cache-sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  const cache = await caches.open(CACHE_NAME);
  let completedBytes = status.installedBytes;

  for (let index = 0; index < catalog.artifacts.length; index += 1) {
    const artifact = catalog.artifacts[index];
    const localUrl = cacheUrl(artifact.path);
    const existing = await cache.match(localUrl);
    if (artifactCacheMatches(existing, artifact)) {
      onProgress({ phase: "cached", artifact, index, completedBytes, totalBytes: catalog.downloadBytes });
      continue;
    }

    const remoteUrl = `https://huggingface.co/${catalog.repository}/resolve/${catalog.revision}/${artifact.path}`;
    onProgress({ phase: "downloading", artifact, index, artifactBytes: 0, completedBytes, totalBytes: catalog.downloadBytes });
    const response = await fetch(remoteUrl, { signal, mode: "cors", credentials: "omit" });
    if (!response.ok || !response.body) throw new Error(`Failed to download ${artifact.path}: HTTP ${response.status}.`);
    const reader = response.body.getReader();
    const integrity = await createArtifactIntegrityVerifier(artifact.sha256);
    let artifactBytes = 0;
    const stream = new ReadableStream({
      async pull(controller) {
        const { done, value } = await reader.read();
        if (done) {
          if (artifactBytes !== artifact.size) {
            controller.error(new Error(`${artifact.path} size mismatch: expected ${artifact.size}, received ${artifactBytes}.`));
            return;
          }
          try {
            const sha256 = integrity.digest();
            onProgress({ phase: "verified", artifact, index, artifactBytes, completedBytes, totalBytes: catalog.downloadBytes, sha256 });
          } catch (error) {
            controller.error(error);
            return;
          }
          controller.close();
          return;
        }
        artifactBytes += value.byteLength;
        integrity.update(value);
        onProgress({ phase: "downloading", artifact, index, artifactBytes, completedBytes, totalBytes: catalog.downloadBytes });
        controller.enqueue(value);
      },
      cancel(reason) { return reader.cancel(reason); }
    });
    const headers = new Headers(response.headers);
    headers.set("content-length", String(artifact.size));
    headers.set("x-sharemyai-size", String(artifact.size));
    if (artifact.sha256) headers.set("x-sharemyai-sha256", artifact.sha256);
    await cache.put(localUrl, new Response(stream, { status: 200, headers }));
    completedBytes += artifact.size;
    onProgress({ phase: "stored", artifact, index, artifactBytes, completedBytes, totalBytes: catalog.downloadBytes });
  }
  return inspectModelPack(catalog, capability);
}

export async function deleteModelPack() {
  if (!("caches" in globalThis)) return false;
  return caches.delete(CACHE_NAME);
}

export async function createArtifactIntegrityVerifier(expectedHash) {
  if (!/^[0-9a-f]{64}$/.test(expectedHash || "")) throw new Error("Artifact is missing a valid pinned SHA-256 hash.");
  const hasher = await createSHA256();
  return {
    update(bytes) {
      hasher.update(bytes);
    },
    digest() {
      const actualHash = hasher.digest();
      if (actualHash !== expectedHash) {
        throw new Error(`Artifact SHA-256 mismatch: expected ${expectedHash}, received ${actualHash}.`);
      }
      return actualHash;
    }
  };
}

export function modelCacheUrl(path) {
  return cacheUrl(path);
}

function cacheUrl(path) {
  return new URL(`${CACHE_PREFIX}${path.split("/").map(encodeURIComponent).join("/")}`, globalThis.location?.origin || "http://localhost").href;
}

function artifactCacheMatches(response, artifact) {
  return Boolean(response
    && Number(response.headers.get("x-sharemyai-size")) === artifact.size
    && response.headers.get("x-sharemyai-sha256") === artifact.sha256);
}

function describeFailedChecks(checks) {
  const failed = Object.entries(checks).filter(([, passed]) => !passed).map(([name]) => name);
  return `Model pack requirements failed: ${failed.join(", ")}.`;
}
